import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { roleAccess } from "../EboPaymentWorking";

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	return [];
};

const normalizeMappedFranchise = (row, index) => ({
	id: row?.id ?? row?._id ?? row?.franchise_id ?? `mapped-franchise-${index + 1}`,
	name: row?.name ?? row?.franchise_name ?? row?.franchise ?? row?.label ?? "",
	stores: getResponseRows(row?.stores || row?.mapped_stores || row?.franchise_stores || row?.store_list || row?.storeList),
});

const getStoreName = (row) => row?.store_name || row?.store_display_name || row?.name || row?.storeName || "";
const getStoreCode = (row) => row?.store_code || row?.code || row?.storeCode || row?.short_code || row?.SHORT_CODE || "";
const getAdmSiteCode = (row) => row?.admsite_code ?? row?.admsiteCode ?? "";
const getStoreStatus = (row) => String(row?.store_status || "enabled").toLowerCase();
const getCommissionPercent = (row) => row?.commission_percent ?? row?.commissionPercentage ?? row?.comm_percent ?? "";
const getEffectiveDate = (row) => row?.effective_date ?? row?.effectiveDate ?? row?.effected_date ?? "";
const formatDisplayDate = (value) => {
	if (!value) return "";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return String(value);
	return parsed.toLocaleDateString("en-CA");
};
const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();
const formatCellValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};
const getDraftStoreValue = (store, index) => String(store?.id ?? store?._id ?? getStoreCode(store) ?? `${getStoreName(store)}-${index}`);

const createNewCommissionRow = (franchiseName = "") => ({
	id: `new-commission-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	franchise_name: franchiseName,
	store_key: "",
	store_name: "",
	store_code: "",
	admsite_code: "",
	commission_percent: "",
	effective_date: "",
	status: "draft",
});

const compactInputSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    height: 28,
    fontSize: 10.5,
    borderRadius: 1,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  "& .MuiInputBase-input": {
    py: 0,
    fontSize: 10.5,
    textAlign: "left",
    boxSizing: "border-box",
  },
	"& .MuiFormControl-root": {
		minWidth: 0,
	},
};

const compactSelectSx = {
	...compactInputSx,
	"& .MuiSelect-select": {
		py: 0,
		fontSize: 10.5,
		textAlign: "left",
		display: "flex",
		alignItems: "center",
		height: "100%",
		boxSizing: "border-box",
	},
};

const downloadCsv = (rows, fileName, headers) => {
	const csvRows = [
		headers.join(","),
		...rows.map((row) =>
			headers
				.map((header) => {
					const value = row?.[header] ?? "";
					const escaped = String(value).replace(/"/g, '""');
					return `"${escaped}"`;
				})
				.join(",")
		),
	];

	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

function COFOCommissionMaster() {
	const [franchiseList, setFranchiseList] = useState([]);
	const [selectedFranchise, setSelectedFranchise] = useState("");
	const [commissionRows, setCommissionRows] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [isAddingCommission, setIsAddingCommission] = useState(false);
	const [newCommissionRows, setNewCommissionRows] = useState([]);
	const [isSavingCommission, setIsSavingCommission] = useState(false);

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	
	const roleAccessData = roleAccess(user);
	const isReadOnlyUser = ["VIEWER", "EDITOR"].includes(roleAccessData?.roles);
	const selectedFranchiseRecord = useMemo(
		() => franchiseList.find((franchise) => String(franchise.id) === String(selectedFranchise)) || null,
		[franchiseList, selectedFranchise]
	);
	const activeFranchiseStores = useMemo(
		() => getResponseRows(selectedFranchiseRecord?.stores).filter((store) => getStoreStatus(store) === "enabled"),
		[selectedFranchiseRecord]
	);

	const updateDraftRow = (rowId, updates) => {
		setNewCommissionRows((currentRows) =>
			currentRows.map((currentRow) => (currentRow.id === rowId ? { ...currentRow, ...updates } : currentRow))
		);
	};

	const removeDraftRow = (rowId) => {
		setNewCommissionRows((currentRows) => {
			if (currentRows.length <= 1) {
				return currentRows;
			}
			return currentRows.filter((currentRow) => currentRow.id !== rowId);
		});
	};

	const columns = useMemo(() => {
		const baseColumns = [
			{
				field: "franchise_name",
				headerName: "FRANCHISE NAME",
				flex: 1.2,
				minWidth: 190,
				headerAlign: "center",
				align: "left",
				valueFormatter: (value) => formatCellValue(value),
			},
			{
				field: "store_name",
				headerName: "STORE NAME",
				flex: 1.35,
				minWidth: 190,
				headerAlign: "center",
				align: "left",
				valueFormatter: (value) => formatCellValue(value),
			},
			{
				field: "store_code",
				headerName: "STORE CODE",
				flex: 0.8,
				minWidth: 110,
				headerAlign: "center",
				align: "left",
				valueFormatter: (value) => formatCellValue(value),
			},
			{
				field: "admsite_code",
				headerName: "ADM SITE CODE",
				flex: 0.85,
				minWidth: 120,
				headerAlign: "center",
				align: "left",
				valueFormatter: (value) => formatCellValue(value),
			},
			{
				field: "commission_percent",
				headerName: "COMMISSION %",
				flex: 0.8,
				minWidth: 125,
				headerAlign: "center",
				align: "center",
				valueFormatter: (value) => formatCellValue(value),
			},
			{
				field: "effective_date",
				headerName: "EFFECTIVE DATE",
				flex: 0.95,
				minWidth: 135,
				headerAlign: "center",
				align: "center",
				valueFormatter: (value) => formatDisplayDate(value),
			},
			{
				field: "status",
				headerName: "STATUS",
				flex: 0.7,
				minWidth: 100,
				headerAlign: "center",
				align: "center",
				renderCell: (params) => {
					const value = String(params?.row?.status || "").toLowerCase();
					const isActive = value === "active";
					return (
						<Box
							sx={{
								px: 1.2,
								py: 0.45,
								borderRadius: 999,
								backgroundColor: isActive ? "#ecfdf5" : "#fef3c7",
								color: isActive ? "#166534" : "#92400e",
								fontSize: 12,
								fontWeight: 800,
								textTransform: "capitalize",
								lineHeight: 1.2,
							}}>
							{formatCellValue(params?.row?.status)}
						</Box>
					);
				},
			},
		];

		return baseColumns;
	}, []);

	const draftColumns = useMemo(
		() => [
			{
				field: "franchise_name",
				headerName: "FRANCHISE NAME",
				flex: 1.15,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
							{params?.row?.franchise_name || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "store_name",
				headerName: "STORE NAME",
				flex: 1.35,
				minWidth: 220,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<TextField
							select
							fullWidth
							size="small"
							sx={compactSelectSx}
							value={params?.row?.store_key || ""}
							onChange={(event) => {
								const nextStore = activeFranchiseStores.find(
									(store, storeIndex) => getDraftStoreValue(store, storeIndex) === String(event.target.value)
								);
								updateDraftRow(params.row.id, {
									store_key: event.target.value,
									store_name: nextStore ? getStoreName(nextStore) : "",
									store_code: nextStore ? getStoreCode(nextStore) : "",
									admsite_code: nextStore ? getAdmSiteCode(nextStore) : "",
								});
							}}
							>
							{activeFranchiseStores.length === 0 ? (
								<MenuItem value="" disabled>
									No store available
								</MenuItem>
							) : (
								activeFranchiseStores.map((store, storeIndex) => {
									const value = getDraftStoreValue(store, storeIndex);
									return (
										<MenuItem key={value} value={value}>
											{getStoreName(store)}
										</MenuItem>
									);
								})
							)}
						</TextField>
					</Box>
				),
			},
			{
				field: "store_code",
				headerName: "STORE CODE",
				flex: 0.78,
				minWidth: 120,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<Typography variant="body2" sx={{ fontWeight: 600, color: "#2563eb" }}>
							{params?.row?.store_code || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "admsite_code",
				headerName: "ADM SITE CODE",
				flex: 0.8,
				minWidth: 120,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<Typography variant="body2">{formatCellValue(params?.row?.admsite_code) || "-"}</Typography>
					</Box>
				),
			},
			{
				field: "commission_percent",
				headerName: "COMMISSION %",
				flex: 0.82,
				minWidth: 140,
				sortable: false,
				headerAlign: "center",
				align: "center",
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<TextField
							fullWidth
							size="small"
							type="number"
							value={params?.row?.commission_percent ?? ""}
							onChange={(event) => updateDraftRow(params.row.id, { commission_percent: event.target.value })}
							inputProps={{ min: 0, step: "0.01" }}
							sx={compactInputSx}
						/>
					</Box>
				),
			},
			{
				field: "effective_date",
				headerName: "EFFECTIVE DATE",
				flex: 0.95,
				minWidth: 155,
				sortable: false,
				headerAlign: "center",
				align: "center",
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<TextField
							fullWidth
							size="small"
							type="date"
							value={params?.row?.effective_date || ""}
							onChange={(event) => updateDraftRow(params.row.id, { effective_date: event.target.value })}
							InputLabelProps={{ shrink: true }}
							sx={compactInputSx}
						/>
					</Box>
				),
			},
			{
				field: "status",
				headerName: "STATUS",
				flex: 0.65,
				minWidth: 100,
				sortable: false,
				headerAlign: "center",
				align: "center",
				renderCell: () => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
						<Box
							sx={{
								px: 1,
								py: 0.35,
								borderRadius: 999,
								backgroundColor: "#fef3c7",
								color: "#92400e",
								fontSize: 11,
								fontWeight: 800,
								lineHeight: 1.2,
								textTransform: "capitalize",
							}}>
							draft
						</Box>
					</Box>
				),
			},
			{
				field: "action",
				headerName: "ACTION",
				flex: 0.55,
				minWidth: 92,
				sortable: false,
				filterable: false,
				headerAlign: "center",
				align: "center",
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
						<Button
							variant="outlined"
							color="error"
							size="small"
							onClick={() => removeDraftRow(params.row.id)}
							disabled={newCommissionRows.length <= 1}
							sx={{ minWidth: 48, height: 28, px: 0.75, fontSize: 10.5 }}>
							Del
						</Button>
					</Box>
				),
			},
		],
		[activeFranchiseStores, newCommissionRows.length]
	);

	const handleGetFranchiseList = async () => {
		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: "/ebo-payment-working/cofo/mapped-stores",
				headers: { "Content-Type": "application/json" },
				method: "GET",
			});
			const rows = getResponseRows(res?.data).map(normalizeMappedFranchise).filter((row) => row?.name);
			setFranchiseList(rows);
			const initialFranchiseId = rows[0]?.id || "";
			setSelectedFranchise((currentSelected) => currentSelected || initialFranchiseId);
			if (initialFranchiseId) {
				await handleLoadCommissionRows(initialFranchiseId, false);
			}
		} catch (fetchError) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: fetchError?.response?.data?.message || "Failed to fetch franchise list",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		handleGetFranchiseList();
	}, []);

	const buildCommissionRows = (rows, franchiseName, franchiseId) =>
		getResponseRows(rows).map((row, index) => ({
			id: row?.id ?? row?._id ?? `${franchiseId}-${index + 1}`,
			franchise_name: row?.franchise || franchiseName || "",
			store_name: getStoreName(row),
			store_code: getStoreCode(row),
			admsite_code: row?.admsite_code,
			commission_percent: getCommissionPercent(row),
			effective_date: getEffectiveDate(row),
			status: row?.status,
		}));

	const handleLoadCommissionRows = async (franchiseId, msg = false) => {
		if (!franchiseId) {
			setCommissionRows([]);
			return;
		}

		try {
			setLoadingOpen(true);
			setError("");
			setSuccess("");
			const franchiseRecord = franchiseList.find((franchise) => String(franchise.id) === String(franchiseId));
			const res = await pythonVmInstance({
				url: "/ebo-payment-working/cofo/stores-comm",
				headers: { "Content-Type": "application/json" },
				method: "GET",
			});

			const rows = buildCommissionRows(res?.data, franchiseRecord?.name || "", franchiseId);
			setCommissionRows(rows);
			setSuccess(rows.length && msg ? "Commission rows loaded successfully." : msg && "No commission rows found for selected franchise.");
		} catch (fetchError) {
			setCommissionRows([]);
			setError(fetchError?.response?.data?.message || "Failed to fetch commission data.");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleAddCommissionClick = () => {
		if (!selectedFranchiseRecord) {
			setError("Please select a franchise before adding commission.");
			return;
		}
		setError("");
		setSuccess("");
		setIsAddingCommission(true);
		setNewCommissionRows((currentRows) => [...currentRows, createNewCommissionRow(selectedFranchiseRecord?.name || "")]);
	};

	const handleCancelAddCommission = () => {
		setIsAddingCommission(false);
		setNewCommissionRows([]);
	};

	const handleSaveCommission = async () => {
		if (!selectedFranchiseRecord) {
			setError("Please select a franchise before saving commission.");
			return;
		}
		if (!newCommissionRows.length) {
			setError("Please add at least one commission row.");
			return;
		}

		const invalidRow = newCommissionRows.find(
			(row) => !row.store_name || !row.store_code || row.commission_percent === "" || !row.effective_date
		);
		if (invalidRow) {
			setError("Please complete store name, commission percent, and effective date for all rows.");
			return;
		}

		try {
			setIsSavingCommission(true);
			setLoadingOpen(true);
			setError("");
			setSuccess("");

			const stores = newCommissionRows.map((row) => ({
				store_name: row.store_name,
				store_code: row.store_code,
				admsite_code: row.admsite_code,
				commission_percent: Number(row.commission_percent),
				effective_date: row.effective_date,
				store_key: row.store_key,
			}));

			await pythonVmInstance({
				url: "/ebo-payment-working/cofo/stores-comm",
				headers: { "Content-Type": "application/json" },
				method: "POST",
				data: {
					store_type: "cofo",
					email: user?.email || "",
					franchise_name: selectedFranchiseRecord?.name || "",
					franchise_id: selectedFranchiseRecord?.id || "",
					stores,
				},
			});

			await handleLoadCommissionRows(selectedFranchise);
			setIsAddingCommission(false);
			setNewCommissionRows([]);
			setSuccess("Commission rows saved successfully.");
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Commission rows saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (saveError) {
			setError(saveError?.response?.data?.message || "Failed to save commission.");
		} finally {
			setIsSavingCommission(false);
			setLoadingOpen(false);
		}
	};

	const handleFileSelect = (event) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";
		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			setSelectedFile(null);
			setUploadedFileName("");
			setError("Please select a CSV file.");
			return;
		}
		setSelectedFile(file);
		setUploadedFileName(file.name);
		setError("");
		setSuccess("");
	};

	const handleFileUpload = async () => {
		if (!selectedFranchise) {
			setError("Please select a franchise before uploading.");
			return;
		}
		if (!selectedFile) {
			setError("Please select a CSV file to upload.");
			return;
		}

		try {
			setLoadingOpen(true);
			setError("");
			const franchiseRecord = franchiseList.find((franchise) => String(franchise.id) === String(selectedFranchise));
			const form = new FormData();
			form.append("files", selectedFile);
			form.append("email", user?.email || "");
			form.append("store_type", "cofo");
			form.append("franchise_name", franchiseRecord?.name || "");

			await pythonVmInstance.post("/ebo-payment-working/upload-comm", form, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			await handleLoadCommissionRows(selectedFranchise);
			setSelectedFile(null);
			setUploadedFileName("");
			setSuccess("Updated commission uploaded successfully.");
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Updated commission uploaded successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (uploadError) {
			setError(uploadError?.response?.data?.message || "Failed to upload updated commission.");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDownload = () => {
		if (!selectedFranchise) return;
		const franchiseRecord = franchiseList.find((franchise) => String(franchise.id) === String(selectedFranchise));
		const rows = getResponseRows(franchiseRecord?.stores)
			.filter((store) => getStoreStatus(store) === "enabled")
			.map((store) => ({
				franchise: franchiseRecord?.name || "",
				store_name: getStoreName(store),
				store_code: getStoreCode(store),
				admsite_code: store?.admsite_code ?? store?.admsiteCode ?? "",
				commission_percent: getCommissionPercent(store) ?? 0,
				effective_date: getEffectiveDate(store) ?? "",
			}));
		if (!franchiseRecord || !rows.length) {
			setError("No enabled stores found for selected franchise.");
			return;
		}
		const safeName = String(franchiseRecord?.name || selectedFranchise).toLowerCase().replace(/[^a-z0-9]+/g, "_");
		downloadCsv(rows, `${safeName}_cofo_commission_master.csv`, [
			"franchise",
			"store_name",
			"store_code",
			"admsite_code",
			"commission_percent",
			"effective_date",
		]);
	};

	const franchiseCommRecord = commissionRows.filter(
		(franchise) =>
			String(franchise.franchise_name) === String(selectedFranchiseRecord?.name || "")
    );

	const tableRows = isAddingCommission ? newCommissionRows : franchiseCommRecord;

	return (
		<Box width="100%" p={1.5} sx={{ background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)" }}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								COFO Commission Master
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Select a franchise, download its commission sheet, then upload the updated commission file.
							</Typography>
						</Box>

						<TextField
							select
							size="small"
							label="Select Franchise"
							value={selectedFranchise}
							onChange={(event) => {
								setSelectedFranchise(event.target.value);
								setError("");
								setSuccess("");
								setIsAddingCommission(false);
								setNewCommissionRows([]);
							}}
							sx={{
								maxWidth: 320,
								"& .MuiInputBase-root": { height: 38, borderRadius: 1.5 },
								"& .MuiInputBase-input": { py: 0.85, fontSize: 13 },
							}}>
							{franchiseList.length === 0 ? (
								<MenuItem value="" disabled>
									No franchise available
								</MenuItem>
							) : (
								franchiseList.map((franchise) => (
									<MenuItem key={franchise.id} value={franchise.id}>
										{franchise.name}
									</MenuItem>
								))
							)}
						</TextField>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
								gap: 1.5,
							}}>
							<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(59,130,246,0.03)" }}>
								<Stack spacing={1.25}>
									<Box>
										<Typography variant="subtitle2" fontWeight={700}>
											Download Template
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Download the current commission sheet for the selected franchise.
										</Typography>
									</Box>
									<Button
										variant="outlined"
										startIcon={<FileDownloadIcon />}
										onClick={handleDownload}
										disabled={!selectedFranchise || isReadOnlyUser}
										size="small"
										sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
										Download
									</Button>
								</Stack>
							</Paper>

							<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(16,185,129,0.03)" }}>
								<Stack spacing={1.25}>
									<Box>
										<Typography variant="subtitle2" fontWeight={700}>
											Upload Updated File
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Choose the updated CSV and upload it for the selected franchise.
										</Typography>
									</Box>
									<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
										<Button
											component="label"
											variant="outlined"
											startIcon={<UploadFileIcon />}
											disabled={isReadOnlyUser}
											size="small"
											sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
											Select CSV
											<input hidden accept=".csv,text/csv" type="file" onChange={handleFileSelect} disabled={isReadOnlyUser} />
										</Button>
										<Button
											variant="contained"
											onClick={handleFileUpload}
											disabled={!selectedFranchise || !selectedFile ||isReadOnlyUser}
											size="small"
											sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
											Upload Commission
										</Button>
									</Stack>
								</Stack>
							</Paper>
						</Box>

						{uploadedFileName ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
								Selected File: {uploadedFileName}
							</Typography>
						) : null}
						{error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
						{success ? <Alert severity="success" sx={{ py: 0.5 }}>{success}</Alert> : null}
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.25}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={1}
							alignItems={{ sm: "center" }}
							justifyContent="space-between">
							<Typography variant="subtitle1" fontWeight={700}>
								Franchise Store Columns
							</Typography>
							<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
								{isAddingCommission ? (
									<>
										<Button
											variant="contained"
											size="small"
											onClick={handleSaveCommission}
											disabled={isSavingCommission || isReadOnlyUser}>
											{isSavingCommission ? "Saving..." : "Save"}
										</Button>
										<Button variant="outlined" size="small" onClick={handleCancelAddCommission} disabled={isSavingCommission}>
											Cancel
										</Button>
									</>
								) : null}
								<Button
									variant="contained"
									size="small"
									onClick={handleAddCommissionClick}
									disabled={!selectedFranchise || isReadOnlyUser || isSavingCommission}>
									Add Commission
								</Button>
							</Stack>
						</Stack>
						{isAddingCommission ? (
							<Alert severity="info" sx={{ py: 0.5 }}>
								Each click on `Add Commission` creates one new draft row in the table. Use the `Action` column to delete any row.
							</Alert>
						) : null}
						{isAddingCommission ? (
							<Box
								sx={{
									width: "100%",
									height: "350px",
								}}>
								<DataGrid
									rows={newCommissionRows}
									columns={draftColumns}
									disableVirtualization
									disableColumnMenu
									disableRowSelectionOnClick
									hideFooterSelectedRowCount
									density="compact"
									pageSizeOptions={[10, 25, 50]}
									rowHeight={46}
									initialState={{
										pagination: {
											paginationModel: { pageSize: 10, page: 0 },
										},
									}}
									getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "draft-row-even" : "draft-row-odd")}
									sx={{
										border: "1px solid #dbe4f0",
										borderRadius: 3,
										fontSize: 13,
										overflow: "hidden",
										"& .MuiDataGrid-main": {
											overflow: "hidden",
										},
										"& .MuiDataGrid-virtualScroller": {
											overflowX: "hidden",
											overflowY: "auto",
										},
										"& .MuiDataGrid-columnHeader": {
											display: "flex",
											alignItems: "center",
											background: "linear-gradient(180deg, #f8fafc 0%, #eef4fb 100%)",
										},
										"& .MuiDataGrid-columnHeaders": {
											minHeight: 44,
											maxHeight: 44,
											borderBottom: "1px solid #dbe4f0",
											backgroundColor: "#f8fafc",
											alignItems: "center",
										},
										"& .MuiDataGrid-columnHeaderTitleContainer": {
											alignItems: "center",
										},
										"& .MuiDataGrid-columnHeaderTitle": {
											fontWeight: 800,
											fontSize: 12,
											letterSpacing: 0.45,
											color: "#0f172a",
										},
										"& .MuiDataGrid-row": {
											maxHeight: "46px !important",
											minHeight: "46px !important",
											borderBottom: "1px solid #edf2f7",
											alignItems: "center",
										},
										"& .MuiDataGrid-row:hover": {
											backgroundColor: "#f8fbff",
										},
										"& .MuiDataGrid-cell": {
											display: "flex",
											alignItems: "center",
											py: 0,
											lineHeight: "46px",
											whiteSpace: "nowrap",
											overflow: "hidden",
											borderTop: "none",
										},
										"& .MuiDataGrid-cellContent": {
											width: "100%",
											display: "flex",
											alignItems: "center",
											height: "100%",
											textAlign: "left",
											overflow: "hidden",
											textOverflow: "ellipsis",
										},
										"& .MuiDataGrid-columnSeparator": {
											color: "#d7dee8",
										},
										"& .draft-row-even": {
											backgroundColor: "#ffffff",
										},
										"& .draft-row-odd": {
											backgroundColor: "#fbfdff",
										},
										"& .MuiDataGrid-footerContainer": {
											borderTop: "1px solid #dbe4f0",
											backgroundColor: "#fcfdff",
										},
									}}
								/>
							</Box>
						) : (
							<Box
								sx={{
									width: "100%",
									height: "350px",
								}}>
								<DataGrid
									rows={tableRows}
									columns={columns}
									pagination
									disableVirtualization
									disableColumnMenu
									disableRowSelectionOnClick
									density="compact"
									pageSizeOptions={[25, 50, 100]}
									rowHeight={42}
									initialState={{
										pagination: {
											paginationModel: { pageSize: 25, page: 0 },
										},
									}}
									getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "commission-row-even" : "commission-row-odd")}
									sx={{
										border: "1px solid #dbe4f0",
										borderRadius: 3,
										fontSize: 13,
										overflow: "hidden",
										"& .MuiDataGrid-main": {
											overflow: "hidden",
										},
										"& .MuiDataGrid-virtualScroller": {
											overflowX: "hidden",
											overflowY: "auto",
										},
										"& .MuiDataGrid-columnHeader": {
											display: "flex",
											alignItems: "center",
											background: "linear-gradient(180deg, #f8fafc 0%, #eef4fb 100%)",
										},
										"& .MuiDataGrid-row": {
											maxHeight: "46px !important",
											minHeight: "46px !important",
											borderBottom: "1px solid #edf2f7",
											alignItems: "center",
										},
										"& .MuiDataGrid-row:hover": {
											backgroundColor: "#f8fbff",
										},
										"& .MuiDataGrid-cell": {
											py: 0,
											display: "flex",
											alignItems: "center",
											lineHeight: "46px",
											whiteSpace: "nowrap",
											overflow: "hidden",
											borderTop: "none",
										},
										"& .MuiDataGrid-cellContent": {
											width: "100%",
											textAlign: "left",
											overflow: "hidden",
											textOverflow: "ellipsis",
											display: "flex",
											alignItems: "center",
											height: "100%",
										},
										"& .MuiDataGrid-columnHeaders": {
											backgroundColor: "#f8fafc",
											minHeight: 44,
											maxHeight: 44,
											borderBottom: "1px solid #dbe4f0",
										},
										"& .MuiDataGrid-columnHeaderTitle": {
											fontWeight: 800,
											fontSize: 12,
											letterSpacing: 0.6,
											color: "#0f172a",
										},
										"& .commission-row-even": {
											backgroundColor: "#ffffff",
										},
										"& .commission-row-odd": {
											backgroundColor: "#fbfdff",
										},
										"& .MuiDataGrid-columnSeparator": {
											color: "#d7dee8",
										},
										"& .MuiDataGrid-footerContainer": {
											borderTop: "1px solid #dbe4f0",
											backgroundColor: "#fcfdff",
										},
									}}
								/>
							</Box>
						)}
					</Stack>
				</Paper>
			</Stack>
		</Box>
	);
}

export default COFOCommissionMaster;
